import { aCentimos, formatear, type Moneda } from '@mc/comercio'
import { campoDePago, importeDelPago, seEnseña } from '@mc/formularios'
import {
  ArrowLeft,
  ArrowRight,
  CheckCircle2,
  Eye,
  MapPin,
  Monitor,
  RotateCcw,
  Smartphone,
  Tablet,
  X,
} from 'lucide-react'
import {
  useEffect,
  useMemo,
  useRef,
  useState,
  type CSSProperties,
  type KeyboardEvent,
  type ReactNode,
} from 'react'
import { createPortal } from 'react-dom'

import { BotonDeIcono, clases } from '../componentes/ui'
import { CampoDelFormulario, type CampoDePrevia } from './CampoDelFormulario'
import { CLASE_DE_COLUMNAS, columnasDe } from './catalogo'
import { ListaDeDondeSale } from './DondeSale'
import { conTotales, erroresDelPaso, formularioDe, pasosConTitulo } from './previa'

/**
 * La vista previa del formulario **funcionando**: la lógica condicional en vivo, los pasos
 * con su barra de progreso, los cálculos y la validación de lo obligatorio y de los
 * formatos. Todo eso es `@mc/formularios`, el mismo código que valida el núcleo y que pinta
 * la web (§24). Se ve en escritorio, tableta y móvil, con el color de marca de la web.
 *
 * Enseña lo que hay en el editor, también lo que no se ha guardado, y «Enviar» **no guarda
 * nada**: enseña la confirmación configurada.
 */
type Dispositivo = 'escritorio' | 'tableta' | 'movil'

const DISPOSITIVOS: Array<{
  clave: Dispositivo
  etiqueta: string
  icono: ReactNode
  ancho: string
}> = [
  {
    clave: 'escritorio',
    etiqueta: 'Escritorio',
    icono: <Monitor />,
    ancho: 'w-full max-w-[52rem]',
  },
  { clave: 'tableta', etiqueta: 'Tableta', icono: <Tablet />, ancho: 'w-[48rem] max-w-full' },
  { clave: 'movil', etiqueta: 'Móvil', icono: <Smartphone />, ancho: 'w-[24.375rem] max-w-full' },
]

const ENFOCABLES =
  'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])'

export const VistaPrevia = ({
  documento,
  sitio,
  espacio,
  id,
  moneda,
  estiloDeMarca,
  cerrar,
}: {
  cerrar: () => void
  documento: Record<string, unknown>
  espacio: number
  estiloDeMarca: CSSProperties
  id: null | number
  moneda: Moneda
  sitio: number
}) => {
  const [dispositivo, setDispositivo] = useState<Dispositivo>('escritorio')
  const [donde, setDonde] = useState(false)
  const caja = useRef<HTMLDivElement>(null)
  const [vuelta, setVuelta] = useState(0)

  useEffect(() => {
    const antes = document.activeElement instanceof HTMLElement ? document.activeElement : null
    caja.current?.querySelector<HTMLElement>('[data-autofocus]')?.focus()
    const alTeclear = (e: globalThis.KeyboardEvent) => {
      if (e.key === 'Escape') cerrar()
    }
    document.addEventListener('keydown', alTeclear)
    // Mientras está abierta, la página de debajo no se desplaza.
    const desbordamiento = document.body.style.overflow
    document.body.style.overflow = 'hidden'
    return () => {
      document.removeEventListener('keydown', alTeclear)
      document.body.style.overflow = desbordamiento
      antes?.focus()
    }
  }, [cerrar])

  const retenerElFoco = (e: KeyboardEvent<HTMLDivElement>) => {
    if (e.key !== 'Tab' || !caja.current) return
    const enfocables = [...caja.current.querySelectorAll<HTMLElement>(ENFOCABLES)].filter(
      (el) => el.offsetParent !== null,
    )
    if (enfocables.length === 0) return
    const primero = enfocables[0]!
    const ultimo = enfocables[enfocables.length - 1]!
    if (e.shiftKey && document.activeElement === primero) {
      e.preventDefault()
      ultimo.focus()
    } else if (!e.shiftKey && document.activeElement === ultimo) {
      e.preventDefault()
      primero.focus()
    }
  }

  const ancho = DISPOSITIVOS.find((d) => d.clave === dispositivo)!.ancho
  return createPortal(
    <div
      aria-label="Vista previa del formulario"
      aria-modal
      className="fixed inset-0 z-50 flex flex-col bg-fondo"
      onKeyDown={retenerElFoco}
      ref={caja}
      role="dialog"
    >
      <header className="flex flex-wrap items-center gap-3 border-b border-linea bg-papel px-4 py-2.5 md:px-6">
        <div className="flex min-w-0 flex-1 items-center gap-2.5">
          <span className="flex size-8 shrink-0 items-center justify-center rounded-boton bg-violeta-suave text-violeta">
            <Eye aria-hidden className="size-4" />
          </span>
          <div className="min-w-0">
            <p className="truncate font-display text-[15px] font-semibold">Vista previa</p>
            <p className="truncate text-xs text-tenue">
              {String(documento.nombre || 'Formulario sin nombre')}
            </p>
          </div>
        </div>
        <div
          aria-label="Ver como"
          className="flex items-center gap-0.5 rounded-boton border border-linea bg-papel-2 p-0.5"
          role="radiogroup"
        >
          {DISPOSITIVOS.map((d) => (
            <button
              aria-checked={dispositivo === d.clave}
              className={clases(
                'inline-flex h-8 items-center gap-1.5 rounded-[6px] px-2.5 text-[13px] font-medium transition-colors [&>svg]:size-4',
                dispositivo === d.clave
                  ? 'bg-papel text-tinta shadow-suave'
                  : 'text-tenue hover:text-tinta',
              )}
              key={d.clave}
              onClick={() => setDispositivo(d.clave)}
              role="radio"
              title={d.etiqueta}
              type="button"
            >
              {d.icono}
              <span className="hidden sm:inline">{d.etiqueta}</span>
            </button>
          ))}
        </div>
        <div className="flex items-center gap-1">
          {id !== null && (
            <div className="relative">
              <button
                aria-expanded={donde}
                className="inline-flex h-8 items-center gap-1.5 rounded-boton px-2.5 text-[13px] font-medium text-tinta-2 hover:bg-papel-3 hover:text-tinta"
                onClick={() => setDonde((v) => !v)}
                type="button"
              >
                <MapPin aria-hidden className="size-4" />
                <span className="hidden md:inline">Ver dónde sale en tu web</span>
                <span className="md:hidden">Dónde sale</span>
              </button>
              {donde && (
                <div className="absolute top-full right-0 z-10 mt-1.5 w-[min(22rem,calc(100vw-2rem))] rounded-caja border border-linea bg-papel p-4 shadow-alta">
                  <ListaDeDondeSale espacio={espacio} formulario={id} sitio={sitio} />
                </div>
              )}
            </div>
          )}
          <BotonDeIcono data-autofocus etiqueta="Cerrar la vista previa" onClick={cerrar}>
            <X />
          </BotonDeIcono>
        </div>
      </header>
      <p className="border-b border-linea bg-papel-2 px-4 py-2 text-center text-xs text-tenue md:px-6">
        Así lo verá quien lo rellene, con lo que tienes en el editor (también lo que no has
        guardado). Prueba las condiciones y los pasos: en la vista previa no se envía nada.
      </p>
      <div className="flex-1 overflow-y-auto px-3 py-6 sm:px-6 sm:py-10">
        <div
          className={clases(
            '@container mx-auto rounded-caja border border-linea bg-papel shadow-media transition-[width] duration-200',
            ancho,
            dispositivo === 'movil' ? 'rounded-[1.75rem] border-[6px] border-linea-fuerte' : '',
          )}
          style={estiloDeMarca}
        >
          <div
            className={clases(
              dispositivo === 'movil' ? 'px-5 py-7' : 'px-6 py-8 sm:px-10 sm:py-10',
            )}
          >
            <FormularioEnVivo
              documento={documento}
              key={vuelta}
              moneda={moneda}
              reiniciar={() => setVuelta((v) => v + 1)}
              sitio={sitio}
            />
          </div>
        </div>
      </div>
    </div>,
    document.body,
  )
}

const MENSAJE_POR_OMISION = 'Gracias. Hemos recibido tu mensaje.'

const FormularioEnVivo = ({
  documento,
  sitio,
  moneda,
  reiniciar,
}: {
  documento: Record<string, unknown>
  moneda: Moneda
  reiniciar: () => void
  sitio: number
}) => {
  const formulario = useMemo(() => formularioDe(documento), [documento])
  const pasos = useMemo(() => pasosConTitulo(formulario.campos), [formulario])
  const [indice, setIndice] = useState(0)
  const [valores, setValores] = useState<Record<string, unknown>>({})
  const [errores, setErrores] = useState<Record<string, string>>({})
  const [enviado, setEnviado] = useState(false)
  const raiz = useRef<HTMLFormElement>(null)
  const [enfocarError, setEnfocarError] = useState(0)

  const paso = pasos[Math.min(indice, pasos.length - 1)]!
  const totales = conTotales(formulario.campos, valores)
  const visibles = paso.campos.filter((c) => c.tipo !== 'oculto' && seEnseña(c, totales))
  const ocultos = formulario.campos.filter((c) => c.tipo === 'oculto').length
  const ultimo = indice >= pasos.length - 1
  const etiquetaDe = (clave: string) =>
    formulario.campos.find((c) => c.clave === clave)?.etiqueta || clave

  // El foco va al primer campo con error, como en la web.
  useEffect(() => {
    if (!enfocarError || !raiz.current) return
    const primero = raiz.current.querySelector<HTMLElement>(
      '[data-con-error] :is(input, select, textarea, button):not([tabindex="-1"])',
    )
    primero?.focus()
  }, [enfocarError])

  const cambiar = (clave: string, valor: unknown) => {
    setValores((v) => ({ ...v, [clave]: valor }))
    setErrores((e) =>
      Object.fromEntries(
        Object.entries(e).filter(([k]) => k !== clave && !k.startsWith(`${clave}.`)),
      ),
    )
  }

  const seguir = () => {
    const encontrados = erroresDelPaso(formulario, valores, paso.campos)
    setErrores(encontrados)
    if (Object.keys(encontrados).length > 0) {
      setEnfocarError((n) => n + 1)
      return
    }
    if (ultimo) setEnviado(true)
    else setIndice((i) => i + 1)
    raiz.current?.scrollIntoView({ block: 'start', behavior: 'smooth' })
  }

  if (formulario.campos.filter((c) => c.tipo !== 'paso').length === 0) {
    return (
      <p className="py-10 text-center text-sm text-tenue">
        Este formulario todavía no tiene campos. Añádelos en el editor y vuelve a mirar.
      </p>
    )
  }

  if (enviado) {
    const confirmacion = formulario.confirmacion
    const pago = campoDePago(formulario.campos)
    const importe = pago && seEnseña(pago, totales) ? importeDelPago(pago, totales) : null
    return (
      <div className="flex flex-col items-center gap-3 py-8 text-center" role="status">
        <span className="flex size-12 items-center justify-center rounded-full bg-(--marca)/10 text-(--marca)">
          <CheckCircle2 aria-hidden className="size-6" />
        </span>
        {confirmacion?.tipo === 'redireccion' && confirmacion.url ? (
          <p className="max-w-md text-[15px] text-tinta">
            Al enviar, tu web llevaría a{' '}
            <span className="font-mono text-sm">{confirmacion.url}</span>.
          </p>
        ) : (
          <p className="max-w-md text-[15px] whitespace-pre-line text-tinta">
            {confirmacion?.mensaje?.trim() || MENSAJE_POR_OMISION}
          </p>
        )}
        {importe !== null && (
          <p className="text-sm text-tinta-2">
            En tu web, ahora se pasaría a pagar {formatear(aCentimos(importe, moneda), moneda)} con
            la pasarela de la tienda.
          </p>
        )}
        <p className="rounded-pastilla bg-papel-2 px-2.5 py-1 text-xs text-tenue">
          Vista previa: no se ha guardado ninguna respuesta.
        </p>
        <button
          className="mt-2 inline-flex items-center gap-1.5 rounded-lg px-3 py-2 text-sm font-medium text-(--marca) hover:bg-(--marca)/5"
          onClick={reiniciar}
          type="button"
        >
          <RotateCcw aria-hidden className="size-4" /> Volver a empezar
        </button>
      </div>
    )
  }

  return (
    <form
      noValidate
      onSubmit={(e) => {
        e.preventDefault()
        seguir()
      }}
      ref={raiz}
    >
      {pasos.length > 1 && (
        <div className="mb-7">
          <div className="mb-2 flex items-baseline justify-between gap-3 text-sm">
            {/* Sin título, el paso solo dice cuál es, a la izquierda, donde se lee primero. */}
            {paso.titulo && <p className="font-medium text-tinta">{paso.titulo}</p>}
            <p className={clases('shrink-0 tabular-nums', paso.titulo ? 'text-tenue' : 'font-medium text-tinta')}>
              Paso {indice + 1} de {pasos.length}
            </p>
          </div>
          <div
            aria-label={`Paso ${indice + 1} de ${pasos.length}`}
            aria-valuemax={pasos.length}
            aria-valuemin={1}
            aria-valuenow={indice + 1}
            className="h-1.5 overflow-hidden rounded-full bg-papel-3"
            role="progressbar"
          >
            <span
              className="block h-full rounded-full bg-(--marca) transition-[width] duration-300"
              style={{ width: `${((indice + 1) / pasos.length) * 100}%` }}
            />
          </div>
        </div>
      )}

      <div className="grid grid-cols-6 gap-x-4 gap-y-5">
        {visibles.map((campo) => {
          const error =
            errores[campo.clave] ??
            Object.entries(errores).find(([k]) => k.startsWith(`${campo.clave}.`))?.[1]
          return (
            <div
              className={
                CLASE_DE_COLUMNAS[
                  columnasDe({ blockType: campo.tipo, ancho: (campo as CampoDePrevia).ancho })
                ]
              }
              data-con-error={error ? true : undefined}
              key={campo.clave || campo.etiqueta}
            >
              <CampoDelFormulario
                cambiar={(v) => cambiar(campo.clave, v)}
                campo={campo as CampoDePrevia}
                error={campo.tipo === 'repetidor' ? errores[campo.clave] : error}
                errores={errores}
                etiquetaDe={etiquetaDe}
                id="previa"
                modo="vivo"
                moneda={moneda}
                sitio={sitio}
                totales={totales}
                valor={campo.tipo === 'calculado' ? totales[campo.clave] : valores[campo.clave]}
              />
            </div>
          )
        })}
      </div>

      {Object.keys(errores).length > 0 && (
        <p className="mt-5 text-sm font-medium text-error" role="alert">
          Revisa{' '}
          {Object.keys(errores).length === 1
            ? 'el campo marcado'
            : `los ${Object.keys(errores).length} campos marcados`}
          .
        </p>
      )}

      <div className="mt-7 flex flex-wrap items-center justify-between gap-3">
        {indice > 0 ? (
          <button
            className="inline-flex h-11 items-center gap-1.5 rounded-lg border border-linea-fuerte px-4 text-[15px] font-medium text-tinta hover:bg-papel-2"
            onClick={() => setIndice((i) => Math.max(0, i - 1))}
            type="button"
          >
            <ArrowLeft aria-hidden className="size-4" /> Anterior
          </button>
        ) : (
          <span />
        )}
        <button
          className="inline-flex h-11 items-center gap-1.5 rounded-lg bg-(--marca) px-6 text-[15px] font-medium text-(--texto-marca) shadow-suave transition-opacity hover:opacity-90"
          type="submit"
        >
          {ultimo ? 'Enviar' : 'Siguiente'}
          {!ultimo && <ArrowRight aria-hidden className="size-4" />}
        </button>
      </div>

      {ocultos > 0 && (
        <p className="mt-6 border-t border-linea pt-4 text-xs text-tenue">
          Además, al enviar se {ocultos === 1 ? 'guarda un dato' : `guardan ${ocultos} datos`} que
          pone el sistema y no se ven (la página de origen, la campaña…).
        </p>
      )}
    </form>
  )
}
